import {
    IDateRange, IDayJsRange, IDateRangeComposite, IDateRangeCompositeNumbers,
} from '@/interfaces/general/IDateRange.ts';
import { IDate } from '@/interfaces/general/IDate.ts';
import { IDateObject } from '@/interfaces/powerMarkets/IDateObject.ts';
import {
    IDateRangeBase,
    IDateRangeByString,
    IDateRangeByStringWithType,
    IDateRangeByNumberWithType,
    IDateRangeByDateWithType,
    IDateJsDate,
    IDateString,
    IDateNumber,
} from '@/interfaces/general/IDateRangeByString.ts';
import dayjs, { Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { IAdvancedDatePickerModel } from '@/interfaces/general/IAdvancedDatePickerModel.ts';
import { IDatePeriod } from '@/interfaces/powerMarkets/balancing/IDatePeriod.ts';
import { ICombinedAggregateOptions } from '@/interfaces/powerMarkets/charts/IModuleChartConfig.ts';
import { TDatePickerMinimumView } from '@/types/powerMarkets/TDatePickerMinimumView.ts';
import { TDatePickerLabelStyle } from '@/types/powerMarkets/TDatePickerLabelStyle.ts';
import { DateFormats } from '@/constants/DateFormats.ts';

dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export function toDateValue (value: string | IDateString): string {
    return typeof value === 'object' && value !== null && 'value' in value ? value.value : value;
}

export function ddMmYyyyDateStringToSortableNumber (date: string): number {
    if (!/^\d{8}$/.test(date)) return Number.MAX_SAFE_INTEGER;
    return Number(`${date.slice(4)}${date.slice(2, 4)}${date.slice(0, 2)}`);
}

export function compareDdMmYyyyDateStrings (a: string, b: string): number {
    return ddMmYyyyDateStringToSortableNumber(a) - ddMmYyyyDateStringToSortableNumber(b);
}

export interface IGenericTableDateFormatOptions {
    isExcel?: boolean;
    timezoneId?: string;
}

export function formatGenericTableDateValue (
    value: number,
    dateFormat: string | null,
    timeFormat: string,
    options: IGenericTableDateFormatOptions = {},
): string {
    const date = options.timezoneId ? dayjs.unix(value).tz(options.timezoneId) : dayjs.unix(value);

    if (!dateFormat) return date.format(`D MMM YYYY, ${timeFormat}`);

    switch (dateFormat) {
        case 'Month_Year':
            return date.format('MM/YYYY');
        case 'Month':
            return date.format('MMMM YYYY');
        case 'Day':
            return date.format('Do MMMM YYYY');
        case 'Minute':
            return date.format(`Do MMMM YYYY, ${timeFormat}`);
        case 'Minute_NoDate':
            return date.format(timeFormat);
        case 'Day_Short':
            return date.format('Do MMM YYYY');
        case 'Day_Short_With_Minutes':
            return options.isExcel ? date.format('DD/MM/YYYY HH:mm') : date.format(`Do MMM YYYY, ${timeFormat}`);
        case 'Day_Short_With_Minutes_24_Hours':
            return date.format('DD/MM/YYYY HH:mm');
        case 'Day_Short_With_Minutes_No_Year':
            return date.format(`Do MMM, ${timeFormat}`);
        default:
            return date.format(dateFormat) || date.format(`D MMM YYYY, ${timeFormat}`);
    }
}

function updateYears (date: Date, years: number, subtract: boolean): Date {
    const newDate = new Date(date);
    const adjustedYears = subtract ? -years : years;
    newDate.setFullYear(newDate.getFullYear() + adjustedYears);
    return newDate;
}

export function addMonths (date: Date, months: number): Date {
    const newDate = new Date(date);
    newDate.setMonth(newDate.getMonth() + months);
    return newDate;
}

export function addYears (date: Date, years: number): Date {
    return updateYears(date, years, false);
}

export function subtractYears (date: Date, years: number): Date {
    return updateYears(date, years, true);
}

type allDates = string | IDateRange | IDateRangeComposite | IDateObject | IDateRangeByString | IDateRangeByStringWithType | IDateRangeByNumberWithType | IDateRangeByDateWithType | IDate | Date;

// ----- Type Guards for each shape -----

function isIDateRange (input: allDates): input is IDateRange {
    return (
        typeof input === 'object'
        && input !== null
        && 'from' in input
        && input.from instanceof Date
        && 'to' in input
        && input.to instanceof Date
    );
}

function isIDateRangeComposite (input: allDates): input is IDateRangeComposite {
    return (
        typeof input === 'object'
        && input !== null
        && 'fromDay' in input
        && 'fromMonth' in input
        && 'fromYear' in input
        && 'toDay' in input
        && 'toMonth' in input
        && 'toYear' in input
    );
}

export function isIDateObject (input: allDates): input is IDateObject {
    // Must be an object and not null
    if (typeof input !== 'object' || input === null) {
        return false;
    }

    // Must have "from" and "to" properties
    if (!('from' in input) || !('to' in input)) {
        return false;
    }

    // Now it's safe to read input.from and input.to
    const from = (input as { from: unknown }).from;
    const to = (input as { to: unknown }).to;

    // Enact Custom can persist `{ type: 'date' }` with no `value` key. Still a typed date
    // object — must not fall through to picker defaults (rolling today).
    const isTypedBoundary = (boundary: unknown): boolean => (
        typeof boundary === 'object'
        && boundary !== null
        && ('value' in boundary || 'type' in boundary)
    );

    return isTypedBoundary(from) || isTypedBoundary(to);
}
function isIDateRangeByString (input: allDates): input is IDateRangeByString {
    return (
        typeof input === 'object'
        && input !== null
        && 'from' in input
        && typeof input.from === 'string'
        && 'to' in input
        && typeof input.to === 'string'
    );
}

function isIDateRangeByStringWithType (
    input: allDates,
): input is IDateRangeByStringWithType {
    // 1) Must be an object and not null
    if (typeof input !== 'object' || input === null) {
        return false;
    }

    // 2) Must have 'from' and 'to' properties
    if (!('from' in input) || !('to' in input)) {
        return false;
    }

    // We'll cast to a generic shape so TS doesn't complain
    const { from, to } = input as { from: unknown; to: unknown };

    // 3) from must be an object
    if (typeof from !== 'object' || from === null) {
        return false;
    }
    // We'll cast here too, so we can check its properties
    const fromObj = from as { type?: unknown; value?: unknown };

    // 4) to must be an object
    if (typeof to !== 'object' || to === null) {
        return false;
    }
    const toObj = to as { type?: unknown; value?: unknown };

    // 5) Check that fromObj.value and toObj.value are strings
    if (typeof fromObj.value !== 'string' || typeof toObj.value !== 'string') {
        return false;
    }

    // 6) Check the `type` property (strictly if you like)
    // If you only care that `type` is some string, remove these checks.
    const validTypes = ['date', 'dynamicHours', 'dynamic'];
    if (!validTypes.includes(String(fromObj.type))) {
        return false;
    }
    if (!validTypes.includes(String(toObj.type))) {
        return false;
    }

    // If all checks passed, we have IDateRangeByStringWithType
    return true;
}

/**
 * Type guard for IDateRangeByNumberWithType
 *    from: { type: 'date' | 'dynamic', value: number }
 *    to: { type: 'date' | 'dynamic', value: number }
 */
function isIDateRangeByNumberWithType (input: allDates): input is IDateRangeByNumberWithType {
    // 1) Must be a non-null object
    if (typeof input !== 'object' || input === null) {
        return false;
    }

    // 2) Must have 'from' and 'to' properties
    if (!('from' in input) || !('to' in input)) {
        return false;
    }

    // 3) Extract them safely
    const { from, to } = input as { from: unknown; to: unknown };

    // 4) Check each is an object
    if (typeof from !== 'object' || from === null) return false;
    if (typeof to !== 'object' || to === null) return false;

    // 5) Cast to the shape we expect
    const fromObj = from as { type?: unknown; value?: unknown };
    const toObj = to as { type?: unknown; value?: unknown };

    // 6) Validate the 'value' is a number
    if (typeof fromObj.value !== 'number' || typeof toObj.value !== 'number') {
        return false;
    }

    // 7) Validate the 'type' is either 'date' or 'dynamic'
    const validTypes: string[] = ['date', 'dynamic'];
    if (!validTypes.includes(String(fromObj.type))) {
        return false;
    }
    if (!validTypes.includes(String(toObj.type))) {
        return false;
    }

    // Passed all checks
    return true;
}

/**
   * Type guard for IDateRangeByDateWithType
   *    from: { type: 'date', value: Date }
   *    to: { type: 'date', value: Date }
   */
function isIDateRangeByDateWithType (input: allDates): input is IDateRangeByDateWithType {
    // 1) Must be a non-null object
    if (typeof input !== 'object' || input === null) {
        return false;
    }

    // 2) Must have 'from' and 'to' properties
    if (!('from' in input) || !('to' in input)) {
        return false;
    }

    // 3) Extract them
    const { from, to } = input as { from: unknown; to: unknown };

    // 4) Check each is an object
    if (typeof from !== 'object' || from === null) return false;
    if (typeof to !== 'object' || to === null) return false;

    // 5) Cast to the shape we expect
    const fromObj = from as { type?: unknown; value?: unknown };
    const toObj = to as { type?: unknown; value?: unknown };

    // 6) Validate the 'value' is a Date
    if (!(fromObj.value instanceof Date) || !(toObj.value instanceof Date)) {
        return false;
    }

    // 7) Validate the 'type' is 'date'
    if (fromObj.type !== 'date') {
        return false;
    }
    if (toObj.type !== 'date') {
        return false;
    }

    // Passed all checks
    return true;
}

function isIDate (input: allDates): input is IDate {
    return (
        typeof input === 'object'
        && input !== null
        && 'day' in input
        && 'month' in input
        && 'year' in input
    );
}

const DATE_BOUNDARY_UNITS = {
    Week: 'week',
    Month: 'month',
    Year: 'year',
} as const;

function resolveNamedBoundary (value: string | number): Dayjs | null {
    if (typeof value !== 'string') return null;

    if (value === 'Last12Months') {
        return dayjs(subtractYears(dayjs().toDate(), 1)).startOf('day');
    }

    const match = value.match(/^(Start|End)(Week|Month|Year)$/);
    if (!match) return null;

    const [, boundaryType, boundaryUnit] = match;
    if (boundaryUnit === 'Week') {
        const currentDate = dayjs();
        const dayOfWeek = currentDate.day();
        const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const startOfWeek = currentDate.add(diffToMonday, 'day').startOf('day');

        return boundaryType === 'Start' ? startOfWeek : startOfWeek.add(6, 'day').endOf('day');
    }

    const unit = DATE_BOUNDARY_UNITS[boundaryUnit as keyof typeof DATE_BOUNDARY_UNITS];
    return boundaryType === 'Start' ? dayjs().startOf(unit) : dayjs().endOf(unit);
}

type TypedDateBoundaryValue = {
    value: string | number | Date;
    type: string;
};

/**
 * Rolling today: `{ type: 'date', value: 0 }` (and 1–5 / null like Enact `value <= 5`).
 * NOT rolling (Custom / fixed):
 * - missing `value` (undefined) — Enact falls through to Custom; JSON often omits the key
 * - empty string `''` — Enact Custom radio stores `value: ''` and findIndex matches it first
 * Only pure numeric strings — not ISO date strings.
 */
function isRollingTodayDateBoundary (type: string | undefined, value: unknown): boolean {
    if (type !== 'date') return false;
    if (value === undefined) return false;
    if (value instanceof Date) return false;
    if (value === null) return true;
    if (typeof value === 'number') {
        return !Number.isNaN(value) && value <= 5;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        // Empty string is Custom in Enact (matches the Custom radio's value: ''), not Today.
        if (trimmed === '') return false;
        if (!/^-?\d+$/.test(trimmed)) return false;
        const numericValue = Number(trimmed);
        return !Number.isNaN(numericValue) && numericValue <= 5;
    }
    return false;
}

function shouldResolveRelativeDateRange (fromObject: TypedDateBoundaryValue, toObject: TypedDateBoundaryValue): boolean {
    return [fromObject, toObject].some(({ type, value }) => type === 'dynamic'
        || resolveNamedBoundary(typeof value === 'string' || typeof value === 'number' ? value : '') !== null
        || isRollingTodayDateBoundary(type, value));
}

function resolveAbsoluteDateBoundary (
    boundary: TypedDateBoundaryValue,
    emptyDateFallback: 'startOfDay' | 'endOfDay',
): Dayjs | null {
    if (boundary.value === 'none') return null;

    const valueMissing = boundary.value == null || boundary.value === '';
    if (valueMissing && boundary.type === 'date') {
        return emptyDateFallback === 'startOfDay' ? dayjs().startOf('day') : dayjs().endOf('day');
    }
    if (valueMissing) return null;

    return dayjs(boundary.value);
}

function resolveTypedDateRange (
    fromObject: TypedDateBoundaryValue,
    toObject: TypedDateBoundaryValue,
): { from: Dayjs | null; to: Dayjs | null } {
    if (shouldResolveRelativeDateRange(fromObject, toObject)) {
        return resolveDateRange(
            { value: fromObject.value as string | number, type: fromObject.type },
            { value: toObject.value as string | number, type: toObject.type },
        );
    }

    // Absolute date boundaries. Missing value on type `date` → start/end of today (Enact:
    // Custom radio + moment(undefined) → now), not rolling Today.
    return {
        from: resolveAbsoluteDateBoundary(fromObject, 'startOfDay'),
        to: resolveAbsoluteDateBoundary(toObject, 'endOfDay'),
    };
}

export function resolveDateRange (
    fromObject: { value: string | number; type: string },
    toObject: { value: string | number; type: string },
): { from: Dayjs | null; to: Dayjs | null } {
    let from: Dayjs | null = dayjs();
    let to: Dayjs | null = dayjs();

    const namedFromBoundary = resolveNamedBoundary(fromObject.value);
    const namedToBoundary = resolveNamedBoundary(toObject.value);

    if (namedFromBoundary) {
        from = namedFromBoundary;
    } else if (isRollingTodayDateBoundary(fromObject.type, fromObject.value)) {
        from = dayjs().startOf('day');
    } else if (fromObject.type === 'dynamic') {
        from = dayjs().startOf('day').subtract(fromObject.value as number, 'days');
    } else {
        if (fromObject.value === '' || fromObject.value === null) {
            fromObject.value = toObject.value;
        }
        from = fromObject.value === 'none' ? null : dayjs(fromObject.value);
    }

    if (namedToBoundary) {
        to = namedToBoundary;
    } else if (isRollingTodayDateBoundary(toObject.type, toObject.value)) {
        to = dayjs().endOf('day');
    } else if (toObject.type === 'dynamic') {
        to = dayjs().endOf('day').add(toObject.value as number, 'days');
    } else if (toObject.value === '' || toObject.value === null) {
        if (fromObject.value === '' || fromObject.value === null) {
            fromObject.value = 0;
            toObject.value = 0;
            from = dayjs();
        } else {
            toObject.value = fromObject.value;
        }
        to = dayjs();
    } else {
        to = toObject.value === 'none' ? null : dayjs(toObject.value);
    }

    return { from, to };
}

/**
 * Unix bounds for generic table column date filtering. Typed variable presets (e.g. this week)
 * store `from`/`to` as `{ type, value }` without `fromTimeInUnix` / `toTimeInUnix`.
 */
export function getUnixBoundsFromTableDateFilterRange (
    dateRange: IDateRangeByString | IDateRangeByStringWithType | undefined,
): { from: number; to: number } | null {
    if (!dateRange) return null;

    const fromVal = dateRange.from;
    const toVal = dateRange.to;

    if (
        typeof fromVal === 'object'
        && fromVal !== null
        && 'type' in fromVal
        && typeof toVal === 'object'
        && toVal !== null
        && 'type' in toVal
    ) {
        const resolved = resolveDateRange(
            fromVal as { value: string | number; type: string },
            toVal as { value: string | number; type: string },
        );
        if (!resolved.from?.isValid() || !resolved.to?.isValid()) return null;
        return { from: resolved.from.unix(), to: resolved.to.unix() };
    }

    const flat = dateRange as IDateRangeByString;
    if (flat.fromTimeInUnix != null && flat.toTimeInUnix != null) {
        return { from: flat.fromTimeInUnix, to: flat.toTimeInUnix };
    }
    const fromDayjs = dayjs(flat.from);
    const toDayjs = dayjs(flat.to);
    if (!fromDayjs.isValid() || !toDayjs.isValid()) return null;
    return { from: fromDayjs.unix(), to: toDayjs.unix() };
}

function normalizeDayjsRange (
    from: Dayjs | null | undefined,
    to: Dayjs | null | undefined,
    fallbackFrom: Dayjs,
    fallbackTo: Dayjs,
): { from: Dayjs; to: Dayjs } {
    return {
        from: from?.isValid() ? from : fallbackFrom,
        to: to?.isValid() ? to : fallbackTo,
    };
}

export function parseToDayjsRange (input: allDates): { from: Dayjs; to: Dayjs } {
    const fallbackFrom = dayjs().startOf('day');
    const fallbackTo = dayjs().endOf('day');

    // 1) If it's a string
    if (typeof input === 'string') {
        const parsed = dayjs(input);
        return normalizeDayjsRange(parsed, parsed, fallbackFrom, fallbackTo);
    }

    // 2) IDateRange
    if (isIDateRange(input)) {
        return { from: dayjs(input.from), to: dayjs(input.to) };
    }

    // 3) IDateRangeComposite
    if (isIDateRangeComposite(input)) {
        const from = dayjs(`${input.fromYear}-${input.fromMonth}-${input.fromDay}`, 'YYYY-MM-DDTHH:mm:ss.SSS', true);
        const to = dayjs(`${input.toYear}-${input.toMonth}-${input.toDay}`, 'YYYY-MM-DDTHH:mm:ss.SSS', true);
        return normalizeDayjsRange(from, to, fallbackFrom, fallbackTo);
    }

    // 4) IDateObject
    if (isIDateObject(input)) {
        let from = fallbackFrom;
        let to = fallbackTo;
        if (input.from && input.to) {
            const resolved = resolveTypedDateRange(input.from, input.to);
            from = resolved.from || fallbackFrom;
            to = resolved.to || fallbackTo;
        }

        return normalizeDayjsRange(from, to, fallbackFrom, fallbackTo);
    }

    // 5) IDateRangeByString
    if (isIDateRangeByString(input)) {
        const from = dayjs(input.from);
        const to = dayjs(input.to);
        return normalizeDayjsRange(from, to, fallbackFrom, fallbackTo);
    }

    // 6) IDateRangeByStringWithType
    if (isIDateRangeByStringWithType(input)) {
        const resolved = resolveTypedDateRange(input.from, input.to);
        return normalizeDayjsRange(
            resolved.from ?? fallbackFrom,
            resolved.to ?? fallbackTo,
            fallbackFrom,
            fallbackTo,
        );
    }

    // 7) IDateRangeByNumberWithType
    if (isIDateRangeByNumberWithType(input)) {
        const resolved = resolveTypedDateRange(input.from, input.to);
        return normalizeDayjsRange(
            resolved.from ?? fallbackFrom,
            resolved.to ?? fallbackTo,
            fallbackFrom,
            fallbackTo,
        );
    }

    // 8) IDateRangeByDateWithType
    if (isIDateRangeByDateWithType(input)) {
        const from = dayjs(input.from.value);
        const to = dayjs(input.to.value);
        return normalizeDayjsRange(from, to, fallbackFrom, fallbackTo);
    }

    // 9) IDate
    if (isIDate(input)) {
        const from = dayjs(`${input.year}-${input.month}-${input.day}`, 'YYYY-M-D', true);
        return normalizeDayjsRange(from, from, fallbackFrom, fallbackTo);
    }

    // 10) Date
    if (input instanceof Date) {
        const from = dayjs(input);
        return normalizeDayjsRange(from, from, fallbackFrom, fallbackTo);
    }

    // fallback if nothing matches
    return { from: fallbackFrom, to: fallbackTo };
}

export function dateFormatToAnyFormat<T> (
    input: allDates,
    targetType: 'string'
    | 'IDateRange'
    | 'IDateRangeComposite'
    | 'IDateRangeCompositeNumbers'
    | 'IDateObject'
    | 'IDateRangeByString'
    | 'IDateRangeByStringWithType'
    | 'IDateRangeByNumberWithType'
    | 'IDateRangeByDateWithType'
    | 'IDate'
    | 'Date',
): T {
    // 1) First parse the input into Dayjs objects
    let { from, to } = parseToDayjsRange(input);

    const inputBase: IDateRangeBase | undefined = typeof input === 'object' && input !== null ? (input as IDateRangeBase) : undefined;
    const recentHour = Boolean(inputBase?.recentHour);

    if (recentHour && inputBase) {
        let rollFrom = dayjs().subtract(1, 'hour');
        let rollTo = dayjs();
        if (inputBase.fromHours != null) {
            rollFrom = dayjs().subtract(inputBase.fromHours, 'hours');
        }
        if (inputBase.toHours != null) {
            rollTo = dayjs().add(inputBase.toHours, 'hours');
        }
        from = rollFrom;
        to = rollTo;
    }

    const fromHoursOfDay = Math.abs(from.startOf('day').diff(from, 'hour'));
    const toHoursOfDay = Math.abs(to.startOf('day').diff(to, 'hour'));
    const fromTimeInUnix = from.valueOf();
    const toTimeInUnix = to.valueOf();

    let fromHours = fromHoursOfDay;
    if (recentHour && inputBase) {
        fromHours = inputBase.fromHours != null ? inputBase.fromHours : 1;
    }

    let toHours = toHoursOfDay;
    if (recentHour && inputBase) {
        toHours = inputBase.toHours != null ? inputBase.toHours : 1;
    }

    // 2) Then shape the data into the requested output type
    switch (targetType) {
        /**
       * 1) string
       * Return, for example, the "from" date in YYYY-MM-DD format
       */
        case 'string': {
            return {
                from: from.format('YYYY-MM-DDTHH:mm:ss.SSS'),
                to: to.format('YYYY-MM-DDTHH:mm:ss.SSS'),
            } as T;
        }

        case 'Date': {
            return {
                from: from.toDate(),
                to: to.toDate(),
            } as T;
        }

        /**
       * 2) IDateRange
       * Return standard JS Dates for "from" and "to"
       */
        case 'IDateRange': {
            const result: IDateRange = {
                from: from.toDate(),
                to: to.toDate(),
            };
            return result as T;
        }

        /**
       * 3) IDateRangeComposite
       */
        case 'IDateRangeComposite': {
            const result: IDateRangeComposite = {
                fromDay: from.format('DD'),
                fromMonth: from.format('MM'),
                fromYear: from.format('YYYY'),
                toDay: to.format('DD'),
                toMonth: to.format('MM'),
                toYear: to.format('YYYY'),
            };
            return result as T;
        }

        case 'IDateRangeCompositeNumbers': {
            const result: IDateRangeCompositeNumbers = {
                fromDay: from.date(),
                fromMonth: from.month() + 1,
                fromYear: from.year(),
                toDay: to.date(),
                toMonth: to.month() + 1,
                toYear: to.year(),
            };
            return result as T;
        }

        /**
       * 4) IDateObject
       */
        case 'IDateObject': {
            const result: IDateObject = {
                from: {
                    type: 'date',
                    value: from.format('YYYY-MM-DDTHH:mm:ss.SSS'),
                },
                to: {
                    type: 'date',
                    value: to.format('YYYY-MM-DDTHH:mm:ss.SSS'),
                },
                // Hours, etc., can be added if needed
            };
            return result as T;
        }

        /**
       * 5) IDateRangeByString
       */
        case 'IDateRangeByString': {
            const result: IDateRangeByString = {
                from: from.format('YYYY-MM-DDTHH:mm:ss.SSS'),
                to: to.format('YYYY-MM-DDTHH:mm:ss.SSS'),
                recentHour,
                fromHours: fromHours || undefined,
                toHours: toHours || undefined,
                fromTimeInUnix: fromTimeInUnix || undefined,
                toTimeInUnix: toTimeInUnix || undefined,
            };
            if (!recentHour) {
                result.fromHoursForDay = (typeof input === 'object' && input !== null && 'fromHoursForDay' in input && (input as IDateRangeBase).fromHoursForDay)
                    ? (input as IDateRangeBase).fromHoursForDay
                    : from.format('HH:mm');
                result.toHoursForDay = (typeof input === 'object' && input !== null && 'toHoursForDay' in input && (input as IDateRangeBase).toHoursForDay)
                    ? (input as IDateRangeBase).toHoursForDay
                    : to.format('HH:mm');
            }
            return result as T;
        }

        /**
       * 6) IDateRangeByStringWithType
       */
        case 'IDateRangeByStringWithType': {
            const result: IDateRangeByStringWithType = {
                from: {
                    type: 'date',
                    value: from.format('YYYY-MM-DDTHH:mm:ss.SSS'),
                },
                to: {
                    type: 'date',
                    value: to.format('YYYY-MM-DDTHH:mm:ss.SSS'),
                },
                recentHour,
                fromHours: fromHours || undefined,
                toHours: toHours || undefined,
                fromTimeInUnix: fromTimeInUnix || undefined,
                toTimeInUnix: toTimeInUnix || undefined,
            };
            if (!recentHour) {
                result.fromHoursForDay = (typeof input === 'object' && input !== null && 'fromHoursForDay' in input && (input as IDateRangeBase).fromHoursForDay)
                    ? (input as IDateRangeBase).fromHoursForDay
                    : from.format('HH:mm');
                result.toHoursForDay = (typeof input === 'object' && input !== null && 'toHoursForDay' in input && (input as IDateRangeBase).toHoursForDay)
                    ? (input as IDateRangeBase).toHoursForDay
                    : to.format('HH:mm');
            }
            return result as T;
        }

        /**
       * 7) IDateRangeByNumberWithType
       */
        case 'IDateRangeByNumberWithType': {
            const result: IDateRangeByNumberWithType = {
                from: {
                    type: 'date',
                    value: from.valueOf(), // numeric timestamp
                },
                to: {
                    type: 'date',
                    value: to.valueOf(),
                },
                recentHour,
                fromHours: fromHours || undefined,
                toHours: toHours || undefined,
                fromTimeInUnix: fromTimeInUnix || undefined,
                toTimeInUnix: toTimeInUnix || undefined,
            };
            return result as T;
        }

        /**
       * 8) IDateRangeByDateWithType
       */
        case 'IDateRangeByDateWithType': {
            const result: IDateRangeByDateWithType = {
                from: {
                    type: 'date',
                    value: from.toDate(),
                },
                to: {
                    type: 'date',
                    value: to.toDate(),
                },
                recentHour,
                fromHours: fromHours || undefined,
                toHours: toHours || undefined,
                fromTimeInUnix: fromTimeInUnix || undefined,
                toTimeInUnix: toTimeInUnix || undefined,
            };
            return result as T;
        }

        /**
       * 9) IDate
       */
        case 'IDate': {
            const result: IDate = {
                day: from.date(),
                month: from.month() + 1,
                year: from.year(),
                period: null,
            };
            return result as T;
        }

        default:
            throw new Error(`Unsupported target type: ${targetType}`);
    }
}

export const parseToDayjs = (date: string | Date | IDateString | IDateJsDate | undefined | null | IDateRangeByNumberWithType | IDateNumber): Dayjs => {
    if (!date) return dayjs();
    if (date instanceof Date) {
        return dayjs(date);
    } if (typeof date === 'string') {
        return dayjs(date);
    } if ('value' in date) {
        return dayjs(date.value);
    }
    return dayjs();
};

function isThereHourDifference (from: Dayjs, to: Dayjs): boolean {
    if (!from.isValid || !to.isValid) return false;
    const startFrom = from.startOf('day');
    const endTo = to.endOf('day');
    return endTo.diff(to, 'hours') > 0 || startFrom.diff(from, 'hours') > 0;
}

function parseHoursForDay (hoursForDay: string | undefined | null, fallback: string): { hour: number; minute: number } {
    const [hourText, minuteText] = (hoursForDay || fallback).split(':');
    return {
        hour: Number(hourText) || 0,
        minute: Number(minuteText) || 0,
    };
}

/** Full-day when hours-for-day are 00:00–23:59 (calendar Date values may still hold wall-clock time). */
function isFullDayHoursForDay (fromHoursForDay?: string | null, toHoursForDay?: string | null): boolean {
    if (!fromHoursForDay && !toHoursForDay) return false;
    const from = parseHoursForDay(fromHoursForDay, '00:00');
    const to = parseHoursForDay(toHoursForDay, '23:59');
    return from.hour === 0 && from.minute === 0 && to.hour === 23 && to.minute >= 59;
}

function applyHoursForDayToParsedRange (
    from: Dayjs,
    to: Dayjs,
    fromHoursForDay?: string | null,
    toHoursForDay?: string | null,
): { from: Dayjs; to: Dayjs; isHourRangeEnabled: boolean } {
    if (fromHoursForDay || toHoursForDay) {
        if (isFullDayHoursForDay(fromHoursForDay, toHoursForDay)) {
            return {
                from: from.startOf('day'),
                to: to.endOf('day'),
                isHourRangeEnabled: false,
            };
        }

        const fromParts = parseHoursForDay(fromHoursForDay, '00:00');
        const toParts = parseHoursForDay(toHoursForDay, '23:59');
        return {
            from: from.startOf('day').hour(fromParts.hour).minute(fromParts.minute).second(0)
                .millisecond(0),
            to: to.startOf('day').hour(toParts.hour).minute(toParts.minute).second(0)
                .millisecond(0),
            isHourRangeEnabled: true,
        };
    }

    return {
        from,
        to,
        isHourRangeEnabled: isThereHourDifference(from, to),
    };
}

export function generateDatePickerSettings (
    chartConfigDate?: IDateRangeByString | IDateRangeByStringWithType | IDateRangeByDateWithType | IDateRangeByNumberWithType,
    moduleDate?: string | IDateObject | IDateRangeByNumberWithType,
    aggregateOptions?: ICombinedAggregateOptions | null,
): IAdvancedDatePickerModel {
    const aggregateOptionsLocal: ICombinedAggregateOptions | null = aggregateOptions
        ? { ...aggregateOptions }
        : null;

    const datePickerModel: IAdvancedDatePickerModel = {
        dateRange: {
            from: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS'),
            to: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSS'),
        },
        aggregate: aggregateOptionsLocal,
        dateType: 'variable',
        rangeSelectType: 'days',
        isHourRangeEnabled: false,
        hoursBefore: 1,
        hoursAfter: 1,
        daysBefore: 0,
        daysAfter: 0,
    };

    const variableDateStartStrings = ['StartWeek', 'StartMonth', 'StartYear', 'Last12Months'] as const;
    const variableDateEndStrings = ['EndWeek', 'EndMonth', 'EndYear'] as const;

    const isVariableDateStartString = (value: unknown): value is IAdvancedDatePickerModel['variableDateStartString'] => (
        typeof value === 'string' && variableDateStartStrings.includes(value as (typeof variableDateStartStrings)[number])
    );

    const isVariableDateEndString = (value: unknown): value is IAdvancedDatePickerModel['variableDateEndString'] => (
        typeof value === 'string' && variableDateEndStrings.includes(value as (typeof variableDateEndStrings)[number])
    );

    const captureVariableDateMarkers = (fromValue: unknown, toValue: unknown): void => {
        if (isVariableDateStartString(fromValue)) {
            datePickerModel.variableDateStartString = fromValue;
        }
        if (isVariableDateEndString(toValue)) {
            datePickerModel.variableDateEndString = toValue;
        }
    };

    const applyFixedDateHours = (
        parsedFrom: Dayjs,
        parsedTo: Dayjs,
        fromHoursForDay?: string | null,
        toHoursForDay?: string | null,
    ): void => {
        const applied = applyHoursForDayToParsedRange(parsedFrom, parsedTo, fromHoursForDay, toHoursForDay);
        datePickerModel.isHourRangeEnabled = applied.isHourRangeEnabled;
        datePickerModel.dateRange.from = applied.from.format('YYYY-MM-DDTHH:mm:ss.SSS');
        datePickerModel.dateRange.to = applied.to.format('YYYY-MM-DDTHH:mm:ss.SSS');
        if (applied.isHourRangeEnabled) {
            (datePickerModel.dateRange as IDateRangeByString).fromHoursForDay = fromHoursForDay
                ?? applied.from.format('HH:mm');
            (datePickerModel.dateRange as IDateRangeByString).toHoursForDay = toHoursForDay
                ?? applied.to.format('HH:mm');
        }
    };

    if (moduleDate) {
        if (typeof moduleDate === 'string') {
            const parsed = dayjs(moduleDate);
            datePickerModel.dateRange = {
                from: parsed.isValid() ? parsed.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS') : toDateValue(datePickerModel.dateRange.from),
                to: parsed.isValid() ? parsed.endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS') : toDateValue(datePickerModel.dateRange.to),
            };
        } else if (isIDateObject(moduleDate)) {
            if (moduleDate.aggregateOptions) {
                datePickerModel.aggregate = moduleDate.aggregateOptions;
            }

            if (moduleDate.timezone) {
                datePickerModel.timezone = moduleDate.timezone;
            }

            if (moduleDate.periodsPerDay) {
                datePickerModel.periodsPerDay = moduleDate.periodsPerDay;
            }

            if (moduleDate.timezone) {
                datePickerModel.timezone = moduleDate.timezone;
            }

            if (moduleDate.periodsPerDay) {
                datePickerModel.periodsPerDay = moduleDate.periodsPerDay;
            }

            if (moduleDate.recentHour) {
                datePickerModel.dateType = 'variable';
                datePickerModel.rangeSelectType = 'hours';
                datePickerModel.hoursBefore = moduleDate.fromHours ?? 1;
                datePickerModel.hoursAfter = moduleDate.toHours ?? 1;
                return datePickerModel;
            }

            let parsedFrom: Dayjs | null;
            let parsedTo: Dayjs | null;

            if (
                moduleDate.from
                && moduleDate.to
                && typeof moduleDate.from === 'object'
                && typeof moduleDate.to === 'object'
                && 'type' in moduleDate.from
                && 'type' in moduleDate.to
            ) {
                const resolved = resolveTypedDateRange(
                    moduleDate.from as TypedDateBoundaryValue,
                    moduleDate.to as TypedDateBoundaryValue,
                );
                parsedFrom = resolved.from;
                parsedTo = resolved.to;
            } else {
                parsedFrom = moduleDate.from?.value != null && moduleDate.from?.value !== ''
                    ? dayjs(moduleDate.from.value)
                    : dayjs().startOf('day');
                parsedTo = moduleDate.to?.value != null && moduleDate.to?.value !== ''
                    ? dayjs(moduleDate.to.value)
                    : dayjs().endOf('day');
            }

            parsedFrom ??= dayjs().startOf('day');
            parsedTo ??= dayjs().endOf('day');

            if (parsedFrom.isValid()) {
                datePickerModel.dateRange.from = parsedFrom.format('YYYY-MM-DDTHH:mm:ss.SSS');
            }
            if (parsedTo.isValid()) {
                datePickerModel.dateRange.to = parsedTo.format('YYYY-MM-DDTHH:mm:ss.SSS');
            }

            const bothRollingToday = isRollingTodayDateBoundary(moduleDate.from?.type, moduleDate.from?.value)
                && isRollingTodayDateBoundary(moduleDate.to?.type, moduleDate.to?.value);

            if (bothRollingToday) {
                datePickerModel.dateType = 'variable';
                datePickerModel.daysBefore = 0;
                datePickerModel.daysAfter = 0;
                if (moduleDate.fromHoursForDay || moduleDate.toHoursForDay) {
                    if (isFullDayHoursForDay(moduleDate.fromHoursForDay, moduleDate.toHoursForDay)) {
                        datePickerModel.isHourRangeEnabled = false;
                    } else {
                        datePickerModel.isHourRangeEnabled = true;
                        (datePickerModel.dateRange as IDateRangeByString).fromHoursForDay = moduleDate.fromHoursForDay;
                        (datePickerModel.dateRange as IDateRangeByString).toHoursForDay = moduleDate.toHoursForDay;
                    }
                }
                return datePickerModel;
            }

            const keysArray: ['from', 'to'] = ['from', 'to'];

            datePickerModel.dateType = 'fixed';
            // Clear default rolling offsets so fixed models never look like Today (0/0).
            datePickerModel.daysBefore = null;
            datePickerModel.daysAfter = null;

            keysArray.forEach((key) => {
                const type = moduleDate[key]?.type;
                switch (type) {
                    case 'date':
                        break;
                    case 'dynamicHours':
                        datePickerModel.dateType = 'variable';
                        datePickerModel.rangeSelectType = 'hours';
                        datePickerModel.hoursBefore = moduleDate.fromHours ?? 1;
                        datePickerModel.hoursAfter = moduleDate.toHours ?? 1;
                        break;
                    case 'dynamic':
                        datePickerModel.dateType = 'variable';
                        datePickerModel.daysBefore = !Number.isNaN(Number(moduleDate.from?.value)) ? Number(moduleDate.from?.value ?? 1) : null;
                        datePickerModel.daysAfter = !Number.isNaN(Number(moduleDate.to?.value)) ? Number(moduleDate.to?.value ?? 1) : null;

                        if (moduleDate.fromHoursForDay || moduleDate.toHoursForDay) {
                            const isFullDay = isFullDayHoursForDay(
                                moduleDate.fromHoursForDay,
                                moduleDate.toHoursForDay,
                            );
                            datePickerModel.isHourRangeEnabled = !isFullDay;
                            if (!isFullDay) {
                                (datePickerModel.dateRange as IDateRangeByString).fromHoursForDay = moduleDate.fromHoursForDay;
                                (datePickerModel.dateRange as IDateRangeByString).toHoursForDay = moduleDate.toHoursForDay;
                            }
                        } else {
                            datePickerModel.isHourRangeEnabled = isThereHourDifference(parsedFrom, parsedTo);
                        }

                        captureVariableDateMarkers(moduleDate.from?.value, moduleDate.to?.value);
                        break;
                }
            });

            if (datePickerModel.dateType === 'fixed' && parsedFrom?.isValid() && parsedTo?.isValid()) {
                applyFixedDateHours(
                    parsedFrom,
                    parsedTo,
                    moduleDate.fromHoursForDay,
                    moduleDate.toHoursForDay,
                );
            }
        }
    } else if (chartConfigDate) {
        if (isIDateRangeByString(chartConfigDate)) {
            datePickerModel.dateRange = {
                from: dayjs(chartConfigDate.from).format('YYYY-MM-DDTHH:mm:ss.SSS'),
                to: dayjs(chartConfigDate.to).format('YYYY-MM-DDTHH:mm:ss.SSS'),
            };
        } else if (isIDateRangeByStringWithType(chartConfigDate) || isIDateRangeByDateWithType(chartConfigDate) || isIDateRangeByNumberWithType(chartConfigDate)) {
            const { from: parsedFrom, to: parsedTo } = resolveTypedDateRange(chartConfigDate.from, chartConfigDate.to);

            if (parsedFrom?.isValid()) {
                datePickerModel.dateRange.from = parsedFrom.format('YYYY-MM-DDTHH:mm:ss.SSS');
            }
            if (parsedTo?.isValid()) {
                datePickerModel.dateRange.to = parsedTo.format('YYYY-MM-DDTHH:mm:ss.SSS');
            }

            const bothRollingToday = isRollingTodayDateBoundary(chartConfigDate.from?.type, chartConfigDate.from?.value)
                && isRollingTodayDateBoundary(chartConfigDate.to?.type, chartConfigDate.to?.value);

            if (bothRollingToday) {
                datePickerModel.dateType = 'variable';
                datePickerModel.daysBefore = 0;
                datePickerModel.daysAfter = 0;
                return datePickerModel;
            }

            const keysArray: ['from', 'to'] = ['from', 'to'];

            datePickerModel.dateType = 'fixed';
            datePickerModel.daysBefore = null;
            datePickerModel.daysAfter = null;

            keysArray.forEach((key) => {
                const type = chartConfigDate[key]?.type;
                switch (type) {
                    case 'date':
                        break;
                    case 'dynamicHours':
                        datePickerModel.dateType = 'variable';
                        datePickerModel.rangeSelectType = 'hours';
                        datePickerModel.hoursBefore = Number(chartConfigDate.from?.value ?? 1);
                        datePickerModel.hoursAfter = Number(chartConfigDate.to?.value ?? 1);
                        break;
                    case 'dynamic':
                        datePickerModel.dateType = 'variable';
                        datePickerModel.daysBefore = !Number.isNaN(Number(chartConfigDate.from?.value)) ? Number(chartConfigDate.from?.value) : null;
                        datePickerModel.daysAfter = !Number.isNaN(Number(chartConfigDate.to?.value)) ? Number(chartConfigDate.to?.value) : null;
                        captureVariableDateMarkers(chartConfigDate.from?.value, chartConfigDate.to?.value);
                        break;
                }
            });

            if (datePickerModel.dateType === 'fixed' && parsedFrom?.isValid() && parsedTo?.isValid()) {
                applyFixedDateHours(
                    parsedFrom,
                    parsedTo,
                    chartConfigDate.fromHoursForDay,
                    chartConfigDate.toHoursForDay,
                );
            }
        }
    }

    return datePickerModel;
}

function formatCompactDateRange (from: Dayjs, to: Dayjs): string {
    const currentYear = dayjs().year();

    if (from.isSame(to, 'day')) {
        if (from.year() === currentYear) {
            return from.format(DateFormats.COMPACT_DAY_MONTH);
        }
        return from.format(DateFormats.COMPACT_DAY_MONTH_YEAR);
    }

    if (from.isSame(to, 'year')) {
        const fromPart = from.format(DateFormats.COMPACT_DAY_MONTH);
        const toPart = to.format(DateFormats.COMPACT_DAY_MONTH);
        if (from.year() === currentYear) {
            return `${fromPart} - ${toPart}`;
        }
        return `${fromPart} - ${toPart} ${from.format('YY')}`;
    }

    return `${from.format(DateFormats.COMPACT_DAY_MONTH_YEAR)} - ${to.format(DateFormats.COMPACT_DAY_MONTH_YEAR)}`;
}

function getCompactLocalDatePickerSettingsString (
    from: Dayjs,
    to: Dayjs,
    showLimitedSingleDate: boolean | undefined,
    minimumView: TDatePickerMinimumView,
    datePickerSettings: IAdvancedDatePickerModel,
): string {
    if (minimumView === 'month' && datePickerSettings.dateType === 'fixed') {
        if (from.isSame(to, 'month') || showLimitedSingleDate) {
            return from.format(DateFormats.COMPACT_MONTH_YEAR);
        }

        return `${from.format(DateFormats.COMPACT_MONTH_YEAR)} - ${to.format(DateFormats.COMPACT_MONTH_YEAR)}`;
    }

    if (showLimitedSingleDate) {
        return formatCompactDateRange(from, from);
    }

    return formatCompactDateRange(from, to);
}

interface IFormatDateRangeOptions {
    isCompact?: boolean;
    excludeDay?: boolean;
}

export function formatDateRange (
    from: Date | Dayjs | string,
    to: Date | Dayjs | string,
    options: IFormatDateRangeOptions = {},
): string {
    const { isCompact = false, excludeDay = false } = options;
    const fromDay = dayjs(from);
    const toDay = dayjs(to);

    if (excludeDay) {
        if (isCompact) {
            const format = DateFormats.COMPACT_MONTH_YEAR;
            if (fromDay.isSame(toDay, 'month')) {
                return fromDay.format(format);
            }
            return `${fromDay.format(format)} - ${toDay.format(format)}`;
        }

        if (fromDay.isSame(toDay, 'month')) {
            return fromDay.format(DateFormats.SHORT_MONTH_YEAR);
        }
        if (fromDay.isSame(toDay, 'year')) {
            return `${fromDay.format(DateFormats.SHORT_MONTH)} - ${toDay.format(DateFormats.SHORT_MONTH_YEAR)}`;
        }
        return `${fromDay.format(DateFormats.SHORT_MONTH_YEAR)} - ${toDay.format(DateFormats.SHORT_MONTH_YEAR)}`;
    }

    if (!isCompact) {
        const format = DateFormats.FULL_DAY_MONTH_YEAR;
        if (fromDay.isSame(toDay, 'day')) {
            return fromDay.format(format);
        }
        return `${fromDay.format(format)} - ${toDay.format(format)}`;
    }

    return formatCompactDateRange(fromDay, toDay);
}

export function getLocalDatePickerSettingsString (
    datePickerSettings: IAdvancedDatePickerModel | null,
    showLimitedSingleDate?: boolean,
    minimumView: TDatePickerMinimumView = 'day',
    labelStyle: TDatePickerLabelStyle = 'full',
): string {
    if (!datePickerSettings) return '';
    const { from, to } = getDayJsRangeFromDayOnlyDatePicker(datePickerSettings);

    if (datePickerSettings.dateType === 'variable' && datePickerSettings.rangeSelectType === 'hours') {
        return `Previous ${datePickerSettings.hoursBefore}h to Next ${datePickerSettings.hoursAfter}h`;
    }

    if (labelStyle === 'compactNumeric') {
        return getCompactLocalDatePickerSettingsString(from, to, showLimitedSingleDate, minimumView, datePickerSettings);
    }

    if (minimumView === 'month' && datePickerSettings.dateType === 'fixed') {
        if (from.isSame(to, 'month') || showLimitedSingleDate) {
            return from.format(DateFormats.SHORT_MONTH_YEAR);
        }

        return `${from.format(DateFormats.SHORT_MONTH_YEAR)} - ${to.format(DateFormats.SHORT_MONTH_YEAR)}`;
    }

    if (from.isSame(to, 'day') || showLimitedSingleDate) {
        return formatDateRange(from, from);
    }
    return formatDateRange(from, to);
}

export function getDatePeriodString (datePeriod: IDatePeriod): string {
    const date = dayjs(datePeriod.date);
    const periodLabel = `P${datePeriod.period}`;
    return date.isSame(dayjs(), 'day') ? periodLabel : `${date.format('DD MMM YYYY')} - ${periodLabel}`;
}

export function getDateObjectFromDatePicker (settings: IAdvancedDatePickerModel, isTimezone = false): IDateObject {
    const dateObject: IDateObject = {};
    if (settings.aggregate !== undefined) {
        dateObject.aggregateOptions = settings.aggregate ? { ...settings.aggregate } : null;
    }

    if (isTimezone) {
        dateObject.timezone = settings.timezone;
        dateObject.periodsPerDay = settings.periodsPerDay;
        return dateObject;
    }

    if (isIDateRangeByStringWithType(settings.dateRange)) {
        dateObject.from = { ...settings.dateRange.from };
        dateObject.to = { ...(settings.dateRange.to as IDateString) };
        dateObject.recentHour = false;
        if (settings.isHourRangeEnabled && settings.dateRange.fromHoursForDay) {
            dateObject.fromHoursForDay = settings.dateRange.fromHoursForDay;
            dateObject.toHoursForDay = settings.dateRange.toHoursForDay;
        }
    } else if (settings.dateType === 'fixed') {
        dateObject.from = { type: 'date', value: toDateValue(settings.dateRange.from) };
        dateObject.to = { type: 'date', value: toDateValue(settings.dateRange.to) };
        dateObject.recentHour = false;
        if (settings.isHourRangeEnabled) {
            dateObject.fromHoursForDay = dayjs(toDateValue(settings.dateRange.from)).format('HH:mm');
            dateObject.toHoursForDay = dayjs(toDateValue(settings.dateRange.to)).format('HH:mm');
        }
    } else if (settings.rangeSelectType === 'hours') {
        dateObject.from = { type: 'dynamicHours', value: 'HoursBefore' };
        dateObject.to = { type: 'dynamicHours', value: 'HoursAfter' };
        dateObject.fromHours = settings.hoursBefore;
        dateObject.toHours = settings.hoursAfter;
        dateObject.recentHour = true; // not sure about this;
    } else {
        const daysBefore = Number(settings.daysBefore);
        const daysAfter = Number(settings.daysAfter);
        if (daysBefore === 0 && daysAfter === 0) {
            dateObject.from = { type: 'date', value: 0 };
            dateObject.to = { type: 'date', value: 0 };
        } else {
            dateObject.from = { type: 'dynamic', value: `${settings.daysBefore}` };
            dateObject.to = { type: 'dynamic', value: `${settings.daysAfter}` };
        }
        dateObject.recentHour = false;
        if (settings.isHourRangeEnabled && settings.dateRange) {
            const range = settings.dateRange as IDateRangeByString;
            if (range.fromHoursForDay) dateObject.fromHoursForDay = range.fromHoursForDay;
            if (range.toHoursForDay) dateObject.toHoursForDay = range.toHoursForDay;
        }
    }

    dateObject.timezone = settings.timezone;
    dateObject.periodsPerDay = settings.periodsPerDay;

    return dateObject;
}

export function getDateRangeFromDayOnlyDatePicker (settings: IAdvancedDatePickerModel): IDateRange {
    const dayJsRange = getDayJsRangeFromDayOnlyDatePicker(settings);
    return { from: dayJsRange.from.toDate(), to: dayJsRange.to.toDate() };
}

export function getDayJsRangeFromDayOnlyDatePicker (settings: IAdvancedDatePickerModel): IDayJsRange {
    const fallbackFrom = dayjs().startOf('day');
    const fallbackTo = dayjs().endOf('day');

    if (settings.dateType === 'variable') {
        if (settings.rangeSelectType === 'hours') {
            let from = dayjs();
            let to = dayjs();
            if (settings.hoursBefore != null) {
                from = from.subtract(settings.hoursBefore, 'hour');
            }
            if (settings.hoursAfter != null) {
                to = to.add(settings.hoursAfter, 'hour');
            }
            return { from, to };
        }

        if (isIDateRangeByStringWithType(settings.dateRange)) {
            const typed = settings.dateRange;
            const resolved = resolveDateRange(typed.from, typed.to);
            return normalizeDayjsRange(resolved.from, resolved.to, fallbackFrom, fallbackTo);
        }

        if (isIDateRangeByString(settings.dateRange)) {
            const from = dayjs(toDateValue(settings.dateRange.from));
            const to = dayjs(toDateValue(settings.dateRange.to));
            return normalizeDayjsRange(from, to, fallbackFrom, fallbackTo);
        }

        let from = dayjs();
        let to = dayjs();
        if (settings.daysBefore != null) {
            from = from.subtract(settings.daysBefore, 'day');
        }
        if (settings.daysAfter != null) {
            to = to.add(settings.daysAfter, 'day');
        }
        return { from, to };
    }

    const from = dayjs(toDateValue(settings.dateRange.from));
    const to = dayjs(toDateValue(settings.dateRange.to));
    return { from, to };
}

export function areDatePeriodsEqual (date1: IDatePeriod, date2: IDatePeriod): boolean {
    if (!date1 || !date2 || !date1.date || !date2.date) return false;
    let date1ToCompare = date1.date;
    if (typeof date1.date === 'string') {
        date1ToCompare = new Date(date1.date);
    }
    let date2ToCompare = date2.date;
    if (typeof date2.date === 'string') {
        date2ToCompare = new Date(date2.date);
    }
    return (
        date1ToCompare?.getDate() === date2ToCompare?.getDate()
        && date1ToCompare?.getMonth() === date2ToCompare?.getMonth()
        && date1ToCompare?.getFullYear() === date2ToCompare?.getFullYear()
        && date1.period === date2.period
    );
}

export function toLimitedSingleDateObject (date: IDateObject | undefined): IDateObject | undefined {
    if (!date) return undefined;
    return {
        from: date.from,
        to: date.from,
        timezone: date.timezone,
        periodsPerDay: date.periodsPerDay,
    };
}

/** Day count from today back to the same calendar date one year ago (365 or 366). */
export function getLastYearDayOffset (): number {
    const today = dayjs().startOf('day');
    const from = dayjs(subtractYears(today.toDate(), 1)).startOf('day');
    return today.diff(from, 'day');
}

export function getDefaultHistoricAnalysisDateModel (): IAdvancedDatePickerModel {
    return {
        dateType: 'variable',
        rangeSelectType: 'days',
        isHourRangeEnabled: false,
        hoursBefore: null,
        hoursAfter: null,
        daysBefore: null,
        daysAfter: null,
        variableDateStartString: 'Last12Months',
        dateRange: {
            from: { type: 'dynamic', value: 'Last12Months' },
            to: { type: 'dynamic', value: '0' },
        },
    };
}
